import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { InfoIcon } from './icons/InfoIcon'
import { AgentSummaryField } from './AgentSummaryField'

/* ─── Types ─── */

export type UrlProbeState = 'idle' | 'checking' | 'healthy' | 'warn' | 'unreachable'
export type KnowledgeSourceType = 'document' | 'web'

export interface KnowledgeSourceFormValue {
  sourceType: KnowledgeSourceType
  displayName: string
  agentSummary: string
  url: string
}

/* ─── Constants / validation ─── */

export const ALLOWED_KNOWLEDGE_MIMES = new Set([
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/markdown',
  'text/x-markdown',
])
export const MAX_KNOWLEDGE_FILE_BYTES = 10 * 1024 * 1024

export function validateKnowledgeFile(file: File): 'globalKnowledgeTab.errors.fileTooLarge' | 'globalKnowledgeTab.errors.unsupportedFile' | null {
  if (file.size > MAX_KNOWLEDGE_FILE_BYTES) return 'globalKnowledgeTab.errors.fileTooLarge'
  const ext = file.name.split('.').pop()?.toLowerCase()
  if (!ALLOWED_KNOWLEDGE_MIMES.has(file.type) && ext !== 'md') return 'globalKnowledgeTab.errors.unsupportedFile'
  return null
}

/* ─── Field-level validation ─── */

type KbErrorKey =
  | 'knowledgeTab.errors.nameRequired'
  | 'knowledgeTab.errors.summaryRequired'
  | 'knowledgeTab.errors.noFile'
  | 'knowledgeTab.errors.noUrl'

export type KbFieldErrors = Partial<Record<'displayName' | 'agentSummary' | 'file' | 'url', KbErrorKey>>

export function getKbFormErrors(
  form: KnowledgeSourceFormValue,
  file: File | null,
  /** Rename mode has no file re-upload and (matching prior behaviour) doesn't
   * require a non-empty URL — only displayName/agentSummary are enforced. */
  opts: { requireFile?: boolean } = {}
): KbFieldErrors {
  const { requireFile = true } = opts
  const errors: KbFieldErrors = {}
  if (!form.displayName.trim()) errors.displayName = 'knowledgeTab.errors.nameRequired'
  if (!form.agentSummary.trim()) errors.agentSummary = 'knowledgeTab.errors.summaryRequired'
  if (requireFile && form.sourceType === 'document' && !file) errors.file = 'knowledgeTab.errors.noFile'
  if (requireFile && form.sourceType === 'web' && !form.url.trim()) errors.url = 'knowledgeTab.errors.noUrl'
  return errors
}

/* ─── PulseDot ─── */

export function PulseDot({ state }: { state: UrlProbeState }) {
  const colors: Record<UrlProbeState, string> = {
    idle: 'var(--text-tertiary)',
    checking: 'var(--warning)',
    healthy: 'var(--accent)',
    warn: 'var(--warning)',
    unreachable: 'var(--danger)',
  }
  return (
    <span style={{
      width: 7, height: 7, borderRadius: '50%',
      background: colors[state],
      display: 'inline-block', flexShrink: 0,
      animation: state === 'checking' ? 'kb-dot-pulse 1s ease-in-out infinite' : undefined,
    }} />
  )
}

/* ─── FieldErrorMessage ─── */

function FieldErrorMessage({ message }: { message: string }) {
  return (
    <div className="field-error" role="alert">
      <InfoIcon size={12} />
      {message}
    </div>
  )
}

/* ─── Document upload section ─── */

interface DocumentUploadFieldProps {
  file: File | null
  fileError?: string | null
  onFileChange: (file: File, error: string | null) => void
  fieldError?: KbErrorKey
}

function DocumentUploadField({ file, fileError, onFileChange, fieldError }: Readonly<DocumentUploadFieldProps>) {
  const { t } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragOver, setIsDragOver] = useState(false)

  function handleFileSelect(f: File) {
    const errKey = validateKnowledgeFile(f)
    onFileChange(f, errKey ? t(errKey) : null)
  }

  return (
    <div className={`form-group${(fieldError || fileError) ? ' form-group-invalid' : ''}`}>
      <label className="form-label">
        {t('knowledgeTab.editor.fileLabel')}
        <span className="required-mark" aria-hidden="true">*</span>
      </label>
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.docx,.md"
        style={{ display: 'none' }}
        aria-hidden="true"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileSelect(f) }}
      />
      <div
        role="button"
        tabIndex={0}
        aria-label={t('knowledgeTab.uploadAria')}
        className="upload-zone"
        onClick={() => fileInputRef.current?.click()}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click() }}
        onDragOver={(e) => { e.preventDefault(); setIsDragOver(true) }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={(e) => {
          e.preventDefault()
          setIsDragOver(false)
          const f = e.dataTransfer.files[0]
          if (f) handleFileSelect(f)
        }}
        style={{
          marginTop: 0,
          borderColor: isDragOver ? 'var(--accent)' : undefined,
          background: isDragOver ? 'var(--accent-subtle)' : undefined,
          transition: 'border-color .15s, background .15s',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          {file ? (
            <p style={{ margin: 0, fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>
              {file.name}
            </p>
          ) : (
            <>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 500 }}>
                {t('knowledgeTab.editor.uploadZone')}
              </p>
              <span className="upload-hint">{t('knowledgeTab.editor.uploadSupported')}</span>
            </>
          )}
        </div>
      </div>
      {fileError && (
        <div role="alert" style={{ fontSize: 12, color: 'var(--danger)', marginTop: 6 }}>
          {fileError}
        </div>
      )}
      {fieldError && !fileError && <FieldErrorMessage message={t(fieldError)} />}
    </div>
  )
}

/* ─── Web URL section ─── */

interface WebUrlFieldProps {
  idPrefix: string
  url: string
  onChange: (url: string) => void
  probeState: UrlProbeState
  fieldError?: KbErrorKey
}

function WebUrlField({ idPrefix, url, onChange, probeState, fieldError }: Readonly<WebUrlFieldProps>) {
  const { t } = useTranslation()
  return (
    <div className={`form-group${fieldError ? ' form-group-invalid' : ''}`}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <label htmlFor={`${idPrefix}-url`} className="form-label" style={{ margin: 0 }}>
          {t('knowledgeTab.editor.urlLabel')}
          <span className="required-mark" aria-hidden="true">*</span>
        </label>
        <PulseDot state={probeState} />
      </div>
      <input
        id={`${idPrefix}-url`}
        type="url"
        value={url}
        onChange={(e) => onChange(e.target.value)}
        placeholder="https://example.com/docs"
        className="form-input"
        style={{ maxWidth: '100%' }}
        aria-invalid={!!fieldError}
        aria-describedby={fieldError ? `${idPrefix}-url-error` : undefined}
      />
      {fieldError && <FieldErrorMessage message={t(fieldError)} />}
    </div>
  )
}

/* ─── KnowledgeSourceForm ─── */

interface KnowledgeSourceFormProps {
  value: KnowledgeSourceFormValue
  onChange: (patch: Partial<KnowledgeSourceFormValue>) => void
  idPrefix: string
  file: File | null
  fileError?: string | null
  onFileChange: (file: File, error: string | null) => void
  probeState: UrlProbeState
  fieldErrors?: KbFieldErrors
  /** 'rename' fixes sourceType (no selector) and drops the file re-upload
   * section for Document sources — a source's type and content aren't
   * editable after creation, only its display name/summary/(for Web) URL. */
  mode?: 'create' | 'rename'
}

export function KnowledgeSourceForm({
  value,
  onChange,
  idPrefix,
  file,
  fileError,
  onFileChange,
  probeState,
  fieldErrors,
  mode = 'create',
}: Readonly<KnowledgeSourceFormProps>) {
  const { t } = useTranslation()
  const showDocumentUpload = value.sourceType === 'document' && mode !== 'rename'
  const showWebUrl = value.sourceType === 'web'

  return (
    <>
      {mode === 'create' && (
        <div className="form-group">
          <label htmlFor={`${idPrefix}-type`} className="form-label">
            {t('knowledgeTab.editor.sourceTypeLabel')}
          </label>
          <select
            id={`${idPrefix}-type`}
            value={value.sourceType}
            onChange={(e) => {
              onChange({ sourceType: e.target.value as KnowledgeSourceType, url: '' })
            }}
            className="form-select"
            style={{ maxWidth: '100%' }}
          >
            <option value="document">{t('knowledgeTab.editor.typeDocument')}</option>
            <option value="web">{t('knowledgeTab.editor.typeWeb')}</option>
          </select>
        </div>
      )}

      <div className={`form-group${fieldErrors?.displayName ? ' form-group-invalid' : ''}`}>
        <label htmlFor={`${idPrefix}-name`} className="form-label">
          {t('knowledgeTab.editor.displayNameLabel')}
          <span className="required-mark" aria-hidden="true">*</span>
        </label>
        <input
          id={`${idPrefix}-name`}
          type="text"
          value={value.displayName}
          onChange={(e) => onChange({ displayName: e.target.value })}
          placeholder={t('knowledgeTab.editor.displayNameOptional')}
          className="form-input"
          style={{ maxWidth: '100%' }}
          aria-invalid={!!fieldErrors?.displayName}
          aria-describedby={fieldErrors?.displayName ? `${idPrefix}-name-error` : undefined}
        />
        {fieldErrors?.displayName && <FieldErrorMessage message={t(fieldErrors.displayName)} />}
      </div>

      <AgentSummaryField
        idPrefix={idPrefix}
        value={value.agentSummary}
        onChange={(agentSummary) => onChange({ agentSummary })}
        labelKey="knowledgeTab.editor.agentSummaryLabel"
        placeholderKey="knowledgeTab.editor.agentSummaryPlaceholder"
        tooltipKey="knowledgeTab.editor.agentSummaryTooltip"
        error={fieldErrors?.agentSummary ? t(fieldErrors.agentSummary) : undefined}
      />

      {showDocumentUpload && (
        <DocumentUploadField
          file={file}
          fileError={fileError}
          onFileChange={onFileChange}
          fieldError={fieldErrors?.file}
        />
      )}

      {showWebUrl && (
        <WebUrlField
          idPrefix={idPrefix}
          url={value.url}
          onChange={(url) => onChange({ url })}
          probeState={probeState}
          fieldError={fieldErrors?.url}
        />
      )}
    </>
  )
}
